import { configureEcho, echo } from '@laravel/echo-vue'
import { api } from '@/api/ApiGateway.js'

/**
 * @TypeDef EchoEventCallback
 * @property {string} event Event to listen for. (be sure to add "." to start of name)
 * @property {function} callback Function to call when event received.
 */

export class Echoes
{
	constructor()
	{
	}

	/**
	 * Create connection to public channel.
	 * @param {string} p_channel Channel name
	 * @param {EchoEventCallback|Array<EchoEventCallback>} [p_event_callbacks] An object or array of objects
	 * of events and the associated callbacks to call for each event.
	 * @param {boolean} [p_exit_if_exists] If the channel connection exits, do nothing. Set false additional listeners may being added;
	 * @returns {import('laravel-echo').PusherChannel<'reverb'>}
	 */
	public(p_channel, p_event_callbacks= [], p_exit_if_exists = true)
	{
		if (!Array.isArray(p_event_callbacks)) p_event_callbacks = [p_event_callbacks];

		//Get or create channel
		let public_channel = null;
		if (!this.echo()?.connector?.channels[p_channel]) //Prevent subscribing to the channel multiple times
		{
			public_channel = this.echo().channel(p_channel);
		}
		else
		{
			public_channel = this.echo().connector?.channels[p_channel];
			if (p_exit_if_exists)
			{
				return public_channel;
			}
		}

		//Add events and callbacks
		for (let i = 0; i < p_event_callbacks.length; i++)
		{
			const event = p_event_callbacks[i].event;

			//Clear event if previously added (otherwise they'll stack)
			public_channel.stopListening(event);
			public_channel.listen(event, p_event_callbacks[i].callback);
		}

		return public_channel;
	}

	/**
	 * Create connection to private channel.
	 * @param {string} p_channel Channel name
	 * @param {EchoEventCallback|Array<EchoEventCallback>} [p_event_callbacks] An object or array of objects
	 * of events and the associated callbacks to call for each event.
	 * @param {boolean} [p_exit_if_exists] If the channel connection exits, do nothing. Set false additional listeners may being added;
	 * @returns {import('laravel-echo').PusherChannel<'reverb'>}
	 */
	private(p_channel, p_event_callbacks= [], p_exit_if_exists = true)
	{
		if (!Array.isArray(p_event_callbacks)) p_event_callbacks = [p_event_callbacks];

		let private_channel = null;

		//Channel name in echo instance
		const echo_name = 'private-' + `${p_channel}`;

		//Get channel
		if (!this.echo()?.connector?.channels[echo_name]) //Prevent subscribing to the channel multiple times
		{
			private_channel = this.echo().private(p_channel);
		}
		else
		{
			private_channel = this.echo().connector.channels[echo_name];
			if (p_exit_if_exists)
			{
				return private_channel;
			}
		}

		//Add events and callbacks
		for (let i = 0; i < p_event_callbacks.length; i++)
		{
			const event = p_event_callbacks[i].event;

			//Clear event if previously added (otherwise they'll stack)
			private_channel.stopListening(event);
			private_channel.listen(event, p_event_callbacks[i].callback);
		}

		return private_channel;
	}

	/**
	 * Untested
	 * @param {string} p_model Ex: "App.Models.User"
	 * @param {string|number} p_id Record id
	 * @param {EchoEventCallback|Array<EchoEventCallback>} p_event_callbacks An object or array of objects
	 * of events and the associated callbacks to call for each event.
	 * @param {boolean} [p_exit_if_exists] If the channel connection exits, do nothing. Set false additional listeners may being added;
	 * @returns {import('laravel-echo').PusherChannel<'reverb'>}
	 */
	model(p_model, p_id, p_event_callbacks= [], p_exit_if_exists = true)
	{
		try
		{
			if (!Array.isArray(p_event_callbacks)) p_event_callbacks = [p_event_callbacks];

			//Channel name in echo instance
			const echo_name = 'private-' + `${p_model}.${p_id}`;

			//Get channel
			let model_channel = null;
			if (!this.echo()?.connector?.channels[echo_name])
			{
				model_channel = this.echo().private(`${p_model}.${p_id}`);
			}
			else
			{
				model_channel = this.echo().connector.channels[echo_name];
				if (p_exit_if_exists)
				{
					return model_channel;
				}
			}

			//Add events and callbacks
			for (let i = 0; i < p_event_callbacks.length; i++)
			{
				const event = p_event_callbacks[i].event;

				//Clear event if previously added (otherwise they'll stack)
				model_channel.stopListening(event);
				model_channel.listen(event, p_event_callbacks[i].callback);
			}
			return model_channel;
		}
		catch(e)
		{
			console.error("Model error", e);
		}
	}

	/**
	 * Create a presence connection
	 * @param {string} p_channel Channel Name
	 * @param {EchoEventCallback|Array<EchoEventCallback>} p_event_callbacks An object or array of objects
	 * of events and the associated callbacks to call for each event.
	 * @param {boolean} [p_exit_if_exists] If the channel connection exits, do nothing. Set false additional listeners may being added;
	 * @returns {import('laravel-echo').PusherChannel<'reverb'>}
	 */
	presence(p_channel, p_event_callbacks = [], p_exit_if_exists = true)
	{
		if (!Array.isArray(p_event_callbacks)) p_event_callbacks = [p_event_callbacks];

		let presence = null;

		//Channel name in echo instance
		const echo_name = 'presence-' + `${p_channel}`;

		//Get or create channel
		if (!this.echo()?.connector?.channels[echo_name])
		{
			presence = this.echo().join(p_channel);
		}
		else
		{
			presence = this.echo()?.connector?.channels[echo_name];
			if (p_exit_if_exists)
			{
				return presence;
			}
		}

		//Add events callbacks
		for (let i = 0; i < p_event_callbacks.length; i++)
		{
			const event = p_event_callbacks[i].event;

			//Clear event if previously added (otherwise they'll stack)
			presence.stopListening(event);
			presence.listen(p_event_callbacks[i].event, p_event_callbacks[i].callback);
		}

		return presence;
	}

	/**
	 * Leave a public, private or presence channel
	 * @param {string} p_channel Channel name
	 */
	leave(p_channel)
	{
		try
		{
			this.echo().leave(p_channel);
		}
		catch (e)
		{
			console.error('useEchoes.leave', e);
		}
	}

	/**
	 * Leave a model channel
	 * @param {string} p_model Channel name or model channel to leave
	 * @param p_id
	 */
	leaveModel(p_model, p_id)
	{
		this.echo().leave(`${p_model}.${p_id}`);
	}

	/**
	 * Stop listening to a specific event.
	 * @param {string} p_channel Channel name
	 * @param {string} p_event Event name (should start with a . if listened with a ".")
	 */
	stopListening(p_channel, p_event)
	{
		if (p_event.indexOf(".") < 0) p_event = "." + p_event;
		if (this.echo()?.connector?.channels[p_channel])
		{
			this.echo().channel(p_channel).stopListening(p_event);
		}
		else if (this.echo()?.connector?.channels['private-' + p_channel])
		{
			this.echo().private(p_channel).stopListening(p_event);
		}
		else if (this.echo()?.connector?.channels['presence-' + p_channel])
		{
			this.echo().join(p_channel).stopListening(p_event);
		}
	}

	/**
	 * Setup configuration. This only needs to be called once in main.js.
	 */
	configure()
	{
		configureEcho({
			broadcaster: "reverb", // pusher
			key: import.meta.env.VITE_REVERB_APP_KEY,
			wsHost: import.meta.env.VITE_REVERB_HOST,
			wsPort: import.meta.env.VITE_REVERB_PORT ?? 8080,
			wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
			forceTLS: false,
			enabledTransports: ['ws', 'wss'],
			disableStats: true,
			cluster: 'mt1',
			authorizer: (channel) => {
				return {
					authorize: (socketId, callback ) => {
						try {
							api.post('/broadcasting/auth', {
									socket_id: socketId,
									channel_name: channel.name,
								})
								.then(response =>
								{
									const data = response?.response?.data ?? response?.data ?? response;
									callback(false, data)
								})
								.catch(error => callback(error))
						} catch (err) {
							callback(true, err);
						}

					}
				}
			},
			// Allows Echo to wait a brief window to capture multiple channel joins
			batchAuth: true,
		});

		//Helps with debugging, allows doing window.Echo().connector.channels for instance
		window.Echo = echo
	}

	/**
	 * Leave all channels
	 */
	leaveAllChannels()
	{
		this.echo().leaveAllChannels();
	}

	/**
	 * Disconnect from websockets (do not use, left here as a warning to fellow travelers)
	 */
	disconnect()
	{
		//Don't do this b/c you'll need to call the config function again.
		//this.echo().disconnect();
	}

	/**
	 * Returns the echo instance.
	 * @returns {{}Echo<BroadcastDriver>}
	 */
	echo()
	{
		return echo();
	}

	/**
	 * Gets the socket id to add to apiGateway calls;
	 * @returns {string}
	 */
	socketId()
	{
		return echo().socketId();
	}
}

export const echoes = new Echoes();